import React, { useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { createFeedbackMessage, deleteFeedbackMessage, fetchFeedbackMessages, fetchMe, updateFeedbackMessage } from '../api';

function formatDateTime(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString();
}

function InfoTooltip() {
  return (
    <div className="relative inline-block group">
      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-200 text-sm font-semibold leading-none cursor-default select-none">i</span>
      <div className="hidden group-hover:block absolute left-0 mt-2 w-[420px] max-w-[80vw] z-10">
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded shadow-lg p-3 text-sm">
          <div className="font-semibold mb-1">Što je ovo?</div>
          <div className="text-gray-700 dark:text-gray-200 whitespace-pre-wrap">
            Comments & Feedback is an internal chat space for tracking suggestions, bugs, and changes while we refine the app.
            {'\n'}Pišite konkretno: Ako je nešto što bi dodali ili promijenili; ako ne, što nije radilo. Napiši što si radio/la i što se dogodilo.
          </div>
        </div>
      </div>
    </div>
  );
}

export default function FeedbackPage() {
  const [me, setMe] = useState(null);

  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [hasMore, setHasMore] = useState(false);

  const [inputBody, setInputBody] = useState('');
  const [sending, setSending] = useState(false);

  const [replyTo, setReplyTo] = useState(null); // { id, author_name, preview }

  const [editingId, setEditingId] = useState(null);
  const [editingBody, setEditingBody] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);

  const scrollRef = useRef(null);

  const isAdminLike = useMemo(() => {
    const role = String(me?.role || '').trim().toLowerCase();
    if (role === 'admin') return true;
    const display = String(me?.display_name || '').trim().toLowerCase();
    if (display === 'admin' || display === 'administrator') return true;
    const email = String(me?.email || '').trim().toLowerCase();
    if (email === 'admin' || email.startsWith('admin@')) return true;
    return false;
  }, [me?.role, me?.display_name, me?.email]);

  const oldestId = useMemo(() => {
    const first = Array.isArray(messages) && messages.length ? messages[0] : null;
    return first?.id != null ? Number(first.id) : null;
  }, [messages]);

  async function loadLatest() {
    setLoading(true);
    setError('');
    try {
      const data = await fetchFeedbackMessages({ limit: 50 }, { redirectOn401: true });
      const rows = Array.isArray(data?.messages) ? data.messages : [];
      setMessages(rows);
      setHasMore(!!data?.has_more);
      setTimeout(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      }, 0);
    } catch (err) {
      setError(err?.message || 'Failed to load messages');
      setMessages([]);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }

  async function loadOlder() {
    if (!oldestId) return;
    const prevScrollHeight = scrollRef.current?.scrollHeight ?? 0;
    const prevScrollTop = scrollRef.current?.scrollTop ?? 0;
    setLoading(true);
    setError('');
    try {
      const data = await fetchFeedbackMessages({ limit: 50, before: oldestId }, { redirectOn401: true });
      const rows = Array.isArray(data?.messages) ? data.messages : [];
      if (rows.length === 0) {
        setHasMore(false);
      } else {
        setMessages(prev => [...rows, ...(Array.isArray(prev) ? prev : [])]);
        setHasMore(!!data?.has_more);
        setTimeout(() => {
          const el = scrollRef.current;
          if (!el) return;
          const nextScrollHeight = el.scrollHeight;
          el.scrollTop = nextScrollHeight - prevScrollHeight + prevScrollTop;
        }, 0);
      }
    } catch (err) {
      setError(err?.message || 'Failed to load older messages');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const u = await fetchMe();
        if (!cancelled) setMe(u || null);
      } catch {
        if (!cancelled) setMe(null);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    loadLatest();
  }, []);

  async function onSend() {
    const body = String(inputBody || '').trim();
    if (!body) return toast.error('Message cannot be empty');

    setSending(true);
    try {
      const created = await createFeedbackMessage(
        { body, reply_to_message_id: replyTo?.id ?? null },
        { redirectOn401: true }
      );
      if (created) {
        setMessages(prev => [...(Array.isArray(prev) ? prev : []), created]);
        setInputBody('');
        setReplyTo(null);
        setTimeout(() => {
          if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }, 0);
      }
    } catch (err) {
      toast.error(err?.message || 'Failed to send');
    } finally {
      setSending(false);
    }
  }

  async function onSaveEdit(messageId) {
    const body = String(editingBody || '').trim();
    if (!body) return toast.error('Message cannot be empty');
    if (body.length > 2000) return toast.error('Message is too long (max 2000)');

    setSavingEdit(true);
    try {
      const updated = await updateFeedbackMessage(messageId, { body }, { redirectOn401: true });
      if (updated) {
        setMessages(prev => (Array.isArray(prev) ? prev : []).map(m => (String(m.id) === String(messageId) ? { ...m, ...updated } : m)));
      }
      setEditingId(null);
      setEditingBody('');
    } catch (err) {
      toast.error(err?.message || 'Failed to edit');
    } finally {
      setSavingEdit(false);
    }
  }

  async function onDeleteMessage(messageId) {
    const ok = globalThis.confirm('Delete this message?');
    if (!ok) return;

    try {
      await deleteFeedbackMessage(messageId, { redirectOn401: true });
      setMessages(prev => {
        const rows = Array.isArray(prev) ? prev : [];
        return rows
          .filter(m => String(m.id) !== String(messageId))
          .map(m => (String(m.reply_to_message_id) === String(messageId) ? { ...m, reply_to_message_id: null, reply_preview: null } : m));
      });
      if (replyTo && String(replyTo.id) === String(messageId)) setReplyTo(null);
    } catch (err) {
      toast.error(err?.message || 'Failed to delete');
    }
  }

  return (
    <div className="p-6 h-full flex flex-col">
      <div className="flex items-center gap-3 mb-4">
        <h1 className="text-xl font-semibold">Comments & Feedback</h1>
        <InfoTooltip />
      </div>

      {error && <div className="text-sm text-red-600 mb-2">{error}</div>}

      <div className="w-full max-w-5xl mx-auto border border-gray-200 dark:border-gray-800 rounded bg-white dark:bg-gray-900 flex flex-col h-[70vh] max-h-[70vh]">
        <div className="p-3 border-b border-gray-200 dark:border-gray-800">
          {hasMore && oldestId != null && (
            <button
              type="button"
              className="text-sm px-3 py-1 rounded border border-gray-300 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50"
              onClick={loadOlder}
              disabled={loading}
            >
              Load older
            </button>
          )}
          {!hasMore && messages.length > 0 && (
            <div className="text-xs text-gray-600 dark:text-gray-400">Beginning of chat</div>
          )}
        </div>

        <div ref={scrollRef} className="flex-1 min-h-0 overflow-x-hidden overflow-y-auto p-4 space-y-3">
          {loading && messages.length === 0 && (
            <div className="text-sm text-gray-600 dark:text-gray-400">Loading…</div>
          )}
          {messages.map(m => {
            const isMine = me?.id && String(m.author_user_id) === String(me.id);
            const isEditing = editingId != null && String(editingId) === String(m.id);
            const canEdit = !isEditing && (isMine || isAdminLike);
            const canDelete = !isEditing && (isMine || isAdminLike);
            return (
              <div key={m.id} className={isMine ? 'flex justify-end' : 'flex justify-start'}>
                <div className={`w-full max-w-[92%] rounded border px-3 py-2 ${isMine ? 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700' : 'bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-800'}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-xs text-gray-600 dark:text-gray-400">
                        <span className="font-medium text-gray-800 dark:text-gray-200">{m.author_name || '—'}</span>
                        {' • '}{formatDateTime(m.created_at)}
                        {m.edited_at ? ' (edited)' : ''}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        className="text-xs px-2 py-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
                        onClick={() => setReplyTo({ id: m.id, author_name: m.author_name || '—', preview: String(m.body || '').slice(0, 80) })}
                        title="Reply"
                      >
                        Reply
                      </button>
                      {isMine && !isEditing && (
                        <button
                          type="button"
                          className="text-xs px-2 py-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
                          title="Edit"
                          onClick={() => {
                            setEditingId(m.id);
                            setEditingBody(m.body || '');
                          }}
                        >
                          ✎
                        </button>
                      )}
                      {canEdit && !isMine && (
                        <button
                          type="button"
                          className="text-xs px-2 py-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
                          title="Edit"
                          onClick={() => {
                            setEditingId(m.id);
                            setEditingBody(m.body || '');
                          }}
                        >
                          ✎
                        </button>
                      )}
                      {canDelete && (
                        <button
                          type="button"
                          className="text-xs px-2 py-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
                          title="Delete"
                          onClick={() => onDeleteMessage(m.id)}
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </div>

                  {!!m.reply_to_message_id && (
                    <div className="mt-2 text-xs border-l-2 border-gray-300 dark:border-gray-700 pl-2 text-gray-700 dark:text-gray-300">
                      {m.reply_preview || '…'}
                    </div>
                  )}

                  {!isEditing && (
                    <div className="mt-1 text-sm text-gray-900 dark:text-gray-100 whitespace-pre-wrap">{m.body}</div>
                  )}

                  {isEditing && (
                    <div className="mt-1">
                      <textarea
                        value={editingBody}
                        onChange={(e) => setEditingBody(e.target.value)}
                        rows={Math.max(2, Math.min(8, String(editingBody || '').split('\n').length + 1))}
                        className="w-full text-sm text-gray-900 dark:text-gray-100 whitespace-pre-wrap leading-5 bg-transparent border border-gray-200 dark:border-gray-700 rounded px-0 py-0 focus:outline-none focus:ring-0 resize-none"
                      />
                      <div className="mt-2 flex justify-end gap-2">
                        <button
                          type="button"
                          className="px-3 py-1 rounded border border-gray-300 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800"
                          onClick={() => {
                            setEditingId(null);
                            setEditingBody('');
                          }}
                          disabled={savingEdit}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="px-3 py-1 rounded bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
                          onClick={() => onSaveEdit(m.id)}
                          disabled={savingEdit}
                        >
                          Save
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          {!loading && messages.length === 0 && (
            <div className="text-sm text-gray-600 dark:text-gray-400">No messages yet.</div>
          )}
        </div>

        <div className="p-4 border-t border-gray-200 dark:border-gray-800">
          {replyTo && (
            <div className="mb-2 flex items-start justify-between gap-2 rounded border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800 px-3 py-2">
              <div className="min-w-0">
                <div className="text-xs text-gray-600 dark:text-gray-300">Replying to {replyTo.author_name}</div>
                <div className="text-xs text-gray-700 dark:text-gray-200 truncate">{replyTo.preview}</div>
              </div>
              <button
                type="button"
                className="text-xs px-2 py-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
                onClick={() => setReplyTo(null)}
              >
                ✕
              </button>
            </div>
          )}

          <div className="flex gap-2">
            <textarea
              value={inputBody}
              onChange={(e) => setInputBody(e.target.value)}
              placeholder="Write a message…"
              className="flex-1 min-h-[48px] max-h-[140px] border rounded px-2 py-1 bg-white dark:bg-gray-800"
            />
            <button
              type="button"
              className="px-4 py-2 rounded bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
              onClick={onSend}
              disabled={sending}
            >
              Send
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
